/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { Alert } from '@/components/ui'

import { Field, showDate, showText } from './fields'

import type { AdminUserDetail } from '../types'

/** Identity and the Auth0 record — read-only. */
export const AccountCard: React.FC<{ user: AdminUserDetail }> = ({ user }) => (
  <section className="card bg-base-100 shadow">
    <div className="card-body">
      <h2 className="card-title text-base">Account</h2>
      <dl className="grid grid-cols-2 gap-4">
        <Field label="User id">
          <span className="font-mono text-xs break-all">{user.uid}</span>
        </Field>
        <Field label="Username">{showText(user.userName)}</Field>
        <Field label="Insights email">{showText(user.email)}</Field>
        {user.auth0 ? (
          <>
            <Field label="Auth0 email">
              {showText(user.auth0.email)}
              {user.auth0.emailVerified === false && (
                <span className="badge badge-warning badge-xs ml-2">unverified</span>
              )}
            </Field>
            <Field label="Last login">{showDate(user.auth0.lastLogin)}</Field>
            <Field label="Last IP">
              <span className="font-mono text-xs">{showText(user.auth0.lastIp)}</span>
            </Field>
            <Field label="Logins">{showText(user.auth0.loginsCount)}</Field>
            <Field label="Created">{showDate(user.auth0.createdAt)}</Field>
            <Field label="Updated">{showDate(user.auth0.updatedAt)}</Field>
            <Field label="Status">
              {user.auth0.blocked ? (
                <span className="badge badge-error badge-sm">Blocked in Auth0</span>
              ) : (
                <span className="badge badge-success badge-sm">Active</span>
              )}
            </Field>
          </>
        ) : (
          <div className="col-span-2">
            {/* A pre-provisioned user has no Auth0 record until their first
                sign-in — that's expected, not a deleted account. */}
            {user.auth0Error === 'not-found' && user.provisionedBy ? (
              <Alert
                type="info"
                message="Auth0 details are unavailable"
                description="The user may not have logged in yet."
              />
            ) : user.auth0Error === 'not-found' ? (
              <Alert
                type="error"
                message="User has been removed from Auth0"
                description={
                  'The Auth0 user behind this profile no longer exists, so they cannot sign in — ' +
                  'including through MyGeotab, which resolves to the same account. The profile is ' +
                  'left over from a deleted user and can safely be ignored or cleaned up.'
                }
              />
            ) : (
              <Alert
                type="warning"
                message="Auth0 details are unavailable"
                description="Auth0 could not be reached. The account may still be fine — try again shortly."
              />
            )}
          </div>
        )}
      </dl>
    </div>
  </section>
)

export default AccountCard
